/**
 * The debug panel (?debug=1): what a tester screenshots when something fails on their phone. Build,
 * browser, which PDF reader was chosen and why, feature checks, and every startup or reading error with
 * its stage, type and message. Messages are shown here only, never in the diagnostic report; files are
 * numbered, never named. A developer tool, in English (like ?dev).
 *
 * This file is on the start path (app.ts records errors with it); the panel's text (src/ui/debugText.ts) is its own
 * chunk, loaded only with ?debug=1 (docs/architecture.md "Size budget").
 */
export const isDebug = (search: string): boolean => new URLSearchParams(search).get('debug') === '1';

export interface DebugEvent {
  time: string;
  /** "file 2 (pdf, 245 KB)" or "page". */
  where: string;
  stage: string;
  name: string;
  message: string;
}

export interface DebugState {
  pdfBuild?: 'modern' | 'legacy' | undefined;
  pdfReader?: 'module' | 'classic' | 'main' | undefined;
  readerConfig?: { legacy: boolean; from: string } | undefined;
  files: { label: string; status: string }[];
  events: readonly DebugEvent[];
}

/** One error as the panel shows it: its type and message (local only). */
export function describeError(err: unknown): { name: string; message: string } {
  if (typeof err === 'string') return { name: 'string', message: err };
  if (err instanceof Error || (typeof DOMException !== 'undefined' && err instanceof DOMException)) {
    // Where it happened: the first frames of the stack (file:line:column), so a screenshot pins the line.
    const frames = (err as Error).stack?.split('\n').map((l) => l.trim()).filter((l) => /:\d+:\d+\)?$/.test(l)).slice(0, 3).map((l) => l.replace(/^.*\//, '')) ?? [];
    return { name: err.name, message: `${err.message}${frames.length ? ` @ ${frames.join(' < ')}` : ''}` };
  }
  if (typeof err === 'object' && err !== null && 'name' in err) return { name: String((err as { name: unknown }).name), message: String((err as { message?: unknown }).message ?? '') };
  return { name: typeof err, message: String(err) };
}
